import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import PasswordFieldWithToggle from '@/components/PasswordFieldWithToggle';
import InvitationAcceptPanel from '@/components/InvitationAcceptPanel';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { platformRolePlanLabel } from '@/utils/capabilities';
import { isPlatformStaffRole } from '@/utils/teamRoles';
import { platformRoleLabel, workspaceRoleLabel } from '@/utils/workspaceRoles';
import { useWorkspace } from '@/context/WorkspaceContext';
import { api } from '@/api/client';

const PASSWORD_HINT = 'At least 8 characters with one letter and one number.';

export default function AccountPage() {
  const { toast } = useToast();
  const { me, refresh } = useMe();
  const { isPersonalMode } = useWorkspace();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite')?.trim() || '';
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const canEditProfile = me?.permissions.canEditOwnProfile ?? false;

  useEffect(() => {
    setDisplayName(me?.name || '');
  }, [me?.name]);

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!canEditProfile || !displayName.trim()) return;
    setSavingProfile(true);
    setProfileError('');
    try {
      await api.me.updateProfile({ name: displayName.trim() });
      await refresh();
      toast('Display name updated', 'success');
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      setPasswordError('All password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    setSavingPassword(true);
    setPasswordError('');
    try {
      await api.me.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast('Password updated successfully', 'success');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Password change failed');
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div>
      <PageHeader title="My account" subtitle="Manage your credentials and profile" />

      <div className="card section account-summary">
        <dl className="account-readonly">
          <div>
            <dt>Email</dt>
            <dd>{me?.email || '—'}</dd>
          </div>
          <div>
            <dt>Platform role</dt>
            <dd>{platformRoleLabel(me?.role)}</dd>
          </div>
          {!isPersonalMode && me?.membershipRole ? (
            <div>
              <dt>Workspace role</dt>
              <dd>{workspaceRoleLabel(me.membershipRole)}</dd>
            </div>
          ) : null}
          <div>
            <dt>{isPlatformStaffRole(me?.role ?? '') ? 'Access' : 'Plan'}</dt>
            <dd>{platformRolePlanLabel(me) || '—'}</dd>
          </div>
        </dl>
      </div>

      {inviteToken ? (
        <div className="card section">
          <InvitationAcceptPanel token={inviteToken} compact />
        </div>
      ) : (
        <div className="card section account-invite-hint">
          <h2>Organization invitations</h2>
          <p className="muted">
            Open an invitation link from your email, or visit{' '}
            <code>/invite/&lt;token&gt;</code> after signing in.
          </p>
          <p className="muted">
            Need a new account first? <Link to="/register">Register</Link>
          </p>
        </div>
      )}

      {canEditProfile && (
        <form className="card section account-form" onSubmit={saveProfile}>
          <h2>Display name</h2>
          {profileError && <div className="alert alert-error account-form__alert">{profileError}</div>}
          <div className="form-field">
            <label htmlFor="account-display-name">Display name</label>
            <input
              id="account-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              required
            />
          </div>
          <div className="account-form__actions">
            <button type="submit" className="btn btn-primary" disabled={savingProfile || !displayName.trim()}>
              {savingProfile ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}

      <form className="card section account-form account-form--password" onSubmit={savePassword}>
        <h2>Change password</h2>
        <p className="muted account-form__hint">{PASSWORD_HINT}</p>
        {passwordError && <div className="alert alert-error account-form__alert">{passwordError}</div>}
        <PasswordFieldWithToggle
          label="Current password"
          value={currentPassword}
          onChange={setCurrentPassword}
          autoComplete="current-password"
          required
          id="account-current-password"
        />
        <PasswordFieldWithToggle
          label="New password"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          required
          id="account-new-password"
        />
        <PasswordFieldWithToggle
          label="Confirm password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
          id="account-confirm-password"
        />
        <div className="account-form__actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
          >
            {savingPassword ? 'Saving…' : 'Save password'}
          </button>
        </div>
      </form>
    </div>
  );
}
